import { SyntheticEvent, useCallback, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';

// Import utils
// TODO: Lodash should no longer be used here
// eslint-disable-next-line no-restricted-imports
import { omit } from 'lodash';
import { formatStringAsUtcMillisOrNull, formatUtcMillisAsString } from '../../helpers/dates';
import { humanReadableFileSize } from '../../helpers/utils';
import { organizationUpdateStudent } from '../../redux/actions/studentsActions';
// eslint-disable-next-line no-restricted-imports
import moment from 'moment';

// Import components
import { Button, DeleteIcon, Dialog, EditIcon, Input, WidgetSizeEnum } from '@wonderschool/common-base-ui';
import BootstrapTable, { ColumnDescription } from 'react-bootstrap-table-next';
// eslint-disable-next-line no-restricted-imports
import { Dropdown, DropdownItemProps, DropdownProps, Header, Icon, Popup, Segment } from 'semantic-ui-react';
import { InlineError, ShowError } from '../../Components/Messages';
import { DatePicker } from '../../Components/Shared/DatePicker';
import MimeIcon from '../../Components/Shared/MimeIcon';
import DeleteFileModal from '../../documents/components/modals/DeleteFileModal';
import { logError } from '../../rollbar';

// Import hooks
import { FileDocument, MalwareStatus } from '@wonderschool/common-base-types';
import { useDownloadFiles, useGetStudentFiles } from '@wonderschool/file-service-client';
import { useTranslation } from 'react-i18next';
import { storage } from '../../api/firebase/firebase';
import { deleteFile, updateDocument } from '../../api/firebase/firestore';
import { documentTestIds } from '../../documents/dictionary';
import { SHARE_TYPE } from '../../documents/types';
import { useOrganization } from '../../hooks/useOrganizations';
import { useUser } from '../../hooks/useUser';
import { useSelectedStudent } from '../studentsHooks';
import { StudentDocumentTableCast } from '../types';

interface StudentTableDocument {
  id: string;
  name: string;
  expirationDate: string;
  tags: string[];
  documentId: string;
}

enum FILE_CLIENT_SOURCE {
  legacy = 'legacy',
  fileService = 'file-service',
}
type DeleteControls = {
  document: FileDocument | StudentDocumentTableCast;
  method: FILE_CLIENT_SOURCE;
  deleting: boolean;
};

const StudentDocumentList = () => {
  const { t } = useTranslation();

  const dispatch = useDispatch();
  const selectedStudent = useSelectedStudent();
  const studentRoomId = useMemo(() => selectedStudent?.rooms?.[0] ?? null, [selectedStudent]);
  const { isParent, isOrganizationAdmin, isLocationAdmin } = useUser();
  const currentOrganization = useOrganization();
  const documents = useMemo(() => selectedStudent?.documents || [], [selectedStudent?.documents]);

  const [modalOpen, setModalOpen] = useState(false);
  const [deleteControls, setDeleteControls] = useState<DeleteControls | null>(null);

  const [data, setData] = useState<StudentTableDocument>({
    id: '',
    name: '',
    expirationDate: '',
    tags: [],
    documentId: '',
  });

  const [rowData, setRowData] = useState({});
  const [errors, setErrors] = useState<{ name?: string; tags?: string; expirationDate?: string }>({});
  const [tagOptions, setTagOptions] = useState<DropdownItemProps[]>([]);

  const { downloadFiles } = useDownloadFiles();

  const { data: fileServiceData, isLoading: isStudentFilesLoading } = useGetStudentFiles({
    studentId: selectedStudent.id,
    roomId: studentRoomId || '',
    locationId: selectedStudent.location,
  });

  const handleAddition = (_e: SyntheticEvent<HTMLElement, Event>, { value }: DropdownProps) => {
    setTagOptions((prev) => [{ text: value, value: value as keyof DropdownItemProps }, ...prev]);
  };

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setData({ ...data, name: e.target.value });
  };

  const handleDropdownChange = (_e: SyntheticEvent<HTMLElement, Event>, { name, value }: DropdownProps) => {
    setData({ ...data, [name as string]: value });
  };

  const handleExpirationDate = (e, { name, value, valid }) => {
    setData((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => (valid ? omit(prev, name) : prev));
  };

  const saveChanges = async () => {
    if (!currentOrganization?.id) return;

    const { documentId, name, expirationDate, tags } = data;

    if (selectedStudent?.id) {
      const studentData = {
        ...selectedStudent,
        documents: {
          ...selectedStudent.documents,
          [documentId]: {
            ...rowData,
            documentId,
            name,
            expirationDate: formatStringAsUtcMillisOrNull(expirationDate),
            tags,
          },
        },
      };

      // Dispatch is since this is an action creator.
      dispatch(organizationUpdateStudent(currentOrganization.id, studentData));

      setTagOptions([]);
      setData({
        id: '',
        name: '',
        expirationDate: '',
        tags: [],
        documentId: '',
      });
    }
  };

  const removeDocument = (refId) => {
    if (!currentOrganization?.id) return;

    if (selectedStudent?.id) {
      const updatedDocuments = omit(selectedStudent?.documents, [refId]);

      // TODO: Create action creator.
      return updateDocument({
        path: `organizations/${currentOrganization.id}/students/${selectedStudent.id}`,
        data: { documents: updatedDocuments },
      });
    }
  };

  const renderFileForm = () => {
    return (
      <div>
        <ShowError errors={errors} />
        {/* Name */}
        <div className="flex flex-col gap-4">
          <div>
            <label className="mb-1 block" htmlFor="name">
              {t('common.name')}
            </label>
            <Input
              id="name"
              name="name"
              type="text"
              value={data?.name}
              onChange={handleNameChange}
              data-testid="name"
            />
            {errors.name && <InlineError text={errors.name} data-testid="name-error" />}
          </div>

          {/* Expiration Date */}
          <div>
            <label className="mb-1 block" htmlFor="expirationDate">
              {t('documents.student.expirationDateLabel')}
            </label>
            <DatePicker
              id="expirationDate"
              name="expirationDate"
              value={data?.expirationDate}
              onChange={handleExpirationDate}
              placeholder={t('documents.student.expirationDatePlaceholder')}
              minDate={moment()}
              error={!!errors.expirationDate}
              closable
            >
              {errors.expirationDate && <InlineError text={errors.expirationDate} data-testid="date-error" />}
            </DatePicker>
          </div>

          {/* Tags */}
          <div>
            <label className="mb-1 block" htmlFor="tags">
              {t('documents.student.tagsLabel')}
            </label>
            <Dropdown
              id="tags"
              name="tags"
              search
              selection
              fluid
              multiple
              allowAdditions
              value={data?.tags || []}
              options={tagOptions}
              onAddItem={handleAddition}
              onChange={handleDropdownChange}
              data-testid="tags"
            />
            {errors.tags && <InlineError text={errors.tags} data-testid="tags-error" />}
          </div>
        </div>
      </div>
    );
  };

  const renderNoData = () => {
    return (
      <Segment basic clearing textAlign="center">
        <Header as={'h3'}>
          <Icon circular inverted name="info" color="grey" />
        </Header>
        <Header as={'h4'} data-testid="no-files-found">
          {t('documents.student.emptyTitle')}
        </Header>
        <p className="text-muted">{t('documents.student.emptyDescription')}</p>
      </Segment>
    );
  };

  const openFile = useCallback(
    async (row) => {
      // check if file-service-document
      if (row.fileStatus === MalwareStatus.CLEAN && row.scanResult?.status === MalwareStatus.CLEAN) {
        try {
          await downloadFiles([row.id]);
        } catch (error) {
          logError('error downloading student files: ', error, {
            studentId: selectedStudent.id,
            organizationId: currentOrganization.id,
          });
        }

        return null;
      }

      // handle legacy documents stored in the student record
      const { path = null, documentId = null } = row?.rowData ?? {};
      if (!path || !documentId) return;
      try {
        // Get a valid URL with token.
        const url = await storage().ref(`${path}/${documentId}`).getDownloadURL();

        if (url?.length) {
          // Open URL in a new tab.
          if (!window.open(url, '_blank')) {
            // Handle if the popup was blocked
            alert('Popup was blocked. Please check your browser settings.');
          }
        }
      } catch (error) {
        console.log(error);
      }
    },
    [downloadFiles, currentOrganization.id, selectedStudent.id]
  );

  const columns = useMemo(() => {
    const columns: ColumnDescription[] = [
      {
        text: '',
        dataField: 'documentId',
        hidden: true,
      },
      {
        text: t('common.name'),
        dataField: 'name',
        sort: true,
        formatter: (cell, row, rowIndex) => (
          <div>
            <Header
              key={rowIndex}
              as="h4"
              className="no-margin"
              icon={row.type ? <MimeIcon mime={row.type} /> : null}
              content={
                <>
                  {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
                  <a
                    rel="noopener noreferrer"
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      openFile(row);
                    }}
                    data-testid="uploaded-doc-name"
                  >
                    {cell}
                  </a>
                  {row.size && <em className="text-muted" data-testid="doc-size">{` (${row.size})`}</em>}
                </>
              }
              data-testid="name-field"
            />
            <div style={{ margin: '5px 0' }}>
              <div className="edited-by text-muted" style={{ fontSize: '12px' }} data-testid="edited-by-name">
                {t('documents.student.editedBy', { editedBy: row.addedBy })}
              </div>
              <div className="uploaded-date text-muted" style={{ fontSize: '12px' }} data-testid="uploaded-date">
                {t('documents.student.uploadedDate', {
                  uploadedDate: row.uploadedDate,
                })}
              </div>
            </div>
          </div>
        ),
        headerStyle: () => {
          return { width: '45%' };
        },
        style: { verticalAlign: 'middle' },
      },
      {
        text: t('documents.student.tagsLabel'),
        dataField: 'tags',
        style: { verticalAlign: 'middle' },
        formatter: (cell, row) => (row.isAdmin ? '' : cell.join(', ')),
        headerStyle: () => {
          return { width: '20%' };
        },
      },
      {
        text: t('documents.student.expirationColumn'),
        dataField: 'expirationDate',
        style: { verticalAlign: 'middle' },
        formatter: (cell) => {
          return formatUtcMillisAsString(cell);
        },
      },
    ];

    // parents can't edit
    if (!isParent) {
      columns.push({
        text: '',
        dataField: '',
        formatter: (_cell, row) => {
          const isLegacy = selectedStudent.documents?.[row.documentId];
          const isIneligibleForDelete =
            studentRoomId &&
            !!Object.keys(row?.sharedFor || {}).find((key) => {
              // `key` format = `room-${roomId}` | `student-${studentId}` | `location-${locationId}`
              const isRoomShare = key.includes(`${SHARE_TYPE.room}-`);
              if (isRoomShare && key === `${SHARE_TYPE.room}-${studentRoomId}`) {
                return true;
              }
            });

          return (
            <div>
              {/* only show EDIT BTN for legacy docs */}
              {isLegacy ? (
                <Button
                  size={WidgetSizeEnum.X_SMALL}
                  onClick={(e) => {
                    e?.stopPropagation();

                    if (documents && row?.documentId) {
                      const selectedDocument = documents[row.documentId];
                      setModalOpen(true);
                      setRowData(selectedDocument);
                      setTagOptions(
                        selectedDocument?.tags?.length
                          ? selectedDocument.tags.map((tag) => ({
                              text: tag,
                              value: tag,
                            }))
                          : []
                      );
                      setData({
                        id: selectedDocument.id,
                        documentId: selectedDocument.documentId,
                        name: selectedDocument.name,
                        tags: selectedDocument.tags,
                        expirationDate: formatUtcMillisAsString(selectedDocument.expirationDate) ?? '',
                      });
                    }
                  }}
                  data-testid="edit-btn"
                  extraClasses="rounded-full ring-0 ring-offset-0 shadow-none display-block"
                >
                  <EditIcon className="size-6" />
                </Button>
              ) : null}

              {/* only show DELETE BTN for authorized staff */}
              {isLocationAdmin || isOrganizationAdmin ? (
                <Popup
                  content={
                    <div data-testid={documentTestIds.studentDocList.deletePopupText}>
                      {t('documents.student.deleteRestricted')}
                    </div>
                  }
                  disabled={!isIneligibleForDelete}
                  trigger={
                    // !! DANGER !!
                    // The div wrapper here is required and must not be removed.
                    // `<Button disabled={true} />` turns off all pointer events, including hover,
                    // which breaks the <Popover /> functionality.
                    <div data-testid={documentTestIds.studentDocList.deleteBtnWrap}>
                      <Button
                        size={WidgetSizeEnum.X_SMALL}
                        disabled={isIneligibleForDelete}
                        onClick={(e) => {
                          if (isIneligibleForDelete) {
                            return null;
                          }
                          e?.stopPropagation();
                          const isLegacy = selectedStudent.documents?.[row.documentId];
                          setDeleteControls({
                            document: row,
                            method: isLegacy ? FILE_CLIENT_SOURCE.legacy : FILE_CLIENT_SOURCE.fileService,
                            deleting: false,
                          });
                        }}
                        data-testid={documentTestIds.studentDocList.btns.delete}
                        // Strange hack... tests not passing without this.
                        extraClasses={`rounded-full ring-0 ring-offset-0 shadow-none display-block ${isIneligibleForDelete ? 'disabled' : ''}`}
                      >
                        <DeleteIcon className="size-6" />
                      </Button>
                    </div>
                  }
                />
              ) : null}
            </div>
          );
        },
        style: { verticalAlign: 'middle' },
        align: 'right',
      });
    }

    return columns;
  }, [
    documents,
    openFile,
    setDeleteControls,
    selectedStudent,
    studentRoomId,
    isParent,
    isLocationAdmin,
    isOrganizationAdmin,
    t,
  ]);

  const tableData: StudentDocumentTableCast[] = useMemo(() => {
    const castNewDocumentListToTableFormat = (documents: FileDocument[]): StudentDocumentTableCast[] => {
      return documents.map((doc) => ({
        documentId: doc.id,
        tags: [],
        addedBy: doc.isAdmin ? t('documents.student.uploadedByWonderschool') : doc.createdBy.displayName,
        uploadedDate: moment(doc.createdAt).format('YYYY-MM-DD - h:mm A'),
        type: doc.file.mimetype,
        ...doc,
      }));
    };
    // handle legacy data
    const tableData: StudentDocumentTableCast[] = documents
      ? Object.keys(documents).map((key) => ({
          documentId: documents[key].documentId,
          name: documents[key].name,
          addedBy: documents[key]?.uploadedBy?.name || 'UNKNOWN',
          type: documents[key].type,
          uploadedDate: formatUtcMillisAsString(documents[key].uploadedDate, 'YYYY-MM-DD - h:mm A'),
          expirationDate: documents[key].expirationDate || null,
          downloadUrl: documents[key].downloadUrl,
          path: documents[key].path,
          refId: documents[key]?.meta?.refId || key,
          size: humanReadableFileSize(documents[key].size) || '',
          tags: documents[key].tags || [],
          rowData: documents[key],
        }))
      : [];

    // due to legacy file storage, we need to combine 2 sources of document data
    if (!isStudentFilesLoading && fileServiceData?.documents) {
      const cast = castNewDocumentListToTableFormat(fileServiceData?.documents ?? []);
      tableData.push(...cast);
    }

    return tableData;
  }, [documents, fileServiceData, isStudentFilesLoading, t]);

  const renderTable = () => {
    return tableData?.length ? (
      <div className="bootstrap-iso" data-testid={documentTestIds.docsTable}>
        <BootstrapTable
          classes="w-auto w-md-100"
          wrapperClasses="table-responsive"
          noDataIndication={t('common.noResultsToDisplay')}
          keyField="documentId"
          bordered={false}
          data={tableData}
          columns={columns}
        />
      </div>
    ) : (
      renderNoData()
    );
  };

  const handleLegacyDelete = async () => {
    const { path, refId } = deleteControls?.document as StudentDocumentTableCast;
    if (path && refId) {
      try {
        await removeDocument(refId);
        await deleteFile({ path, refId });
      } catch (error) {
        console.log('error: ', error);
        setErrors((prev) => ({ ...prev, error }));
      }
    }

    setDeleteControls(null);
  };

  const renderEditModal = () => (
    <Dialog isOpen={modalOpen} onClose={() => setModalOpen(false)} data-testid="edit-student-document">
      <Dialog.Panel>
        <Dialog.Title>{t('documents.student.editDialogTitle')}</Dialog.Title>
        <div className="my-4">{renderFileForm()}</div>
      </Dialog.Panel>
      <Dialog.Actions>
        <div className="grid w-full grid-cols-1 items-center gap-3 sm:grid-cols-2">
          <Button
            onClick={() => setModalOpen(false)}
            label={t('common.cancel')}
            extraClasses="w-full order-2 sm:order-1"
          />
          <Button
            primary
            extraClasses="w-full order-1 sm:order-2"
            label={t('common.update')}
            onClick={() => {
              saveChanges();
              setModalOpen(false);
            }}
          />
        </div>
      </Dialog.Actions>
    </Dialog>
  );

  const renderDeleteModal = () => {
    return (
      <div>
        {!!deleteControls?.document && deleteControls.method === FILE_CLIENT_SOURCE.fileService ? (
          <DeleteFileModal
            isModalOpen={!!deleteControls.document}
            selectedDocument={deleteControls.document as FileDocument}
            student={selectedStudent}
            closeModal={() => setDeleteControls(null)}
          />
        ) : null}
        {!!deleteControls?.document && deleteControls.method === FILE_CLIENT_SOURCE.legacy ? (
          <DeleteFileModal
            isModalOpen={!!deleteControls.document}
            selectedDocument={deleteControls.document as FileDocument}
            student={selectedStudent}
            closeModal={() => setDeleteControls(null)}
            handleLegacyDelete={handleLegacyDelete}
            isLegacy={true}
          />
        ) : null}
      </div>
    );
  };

  return (
    <div>
      {renderDeleteModal()}
      {renderEditModal()}
      {renderTable()}
    </div>
  );
};

export default StudentDocumentList;
